import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { paintingAPI } from '../utils/api';
import { pixelEvents } from '../utils/pixelEvents';
import SEO from '../components/SEO';
import SocialShare from '../components/SocialShare';
import TrustBadges from '../components/TrustBadges';
import config from '../config/config';
import './PaintingDetails.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PaintingDetails = () => {
  const { slug } = useParams();
  const [painting, setPainting] = useState(null);
  const [relatedPaintings, setRelatedPaintings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [selectedPainting, setSelectedPainting] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  useEffect(() => {
    fetchPaintingDetails();
  }, [slug]);

  const fetchPaintingDetails = async () => {
    try {
      const response = await paintingAPI.getById(slug);
      const paintingData = response.data;
      setPainting(paintingData);

      // Fetch related paintings from same category
      if (paintingData?.category) {
        try {
          const allResponse = await paintingAPI.getAll({ status: 'available' });
          const related = allResponse.data
            .filter(p => p.category === paintingData.category && p.id !== paintingData.id)
            .slice(0, 4);
          setRelatedPaintings(related);
        } catch (err) {
          console.error('Error fetching related paintings:', err);
        }
      }
    } catch (error) {
      console.error('Error fetching painting details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleContactClick = (painting) => {
    pixelEvents.contactToBuy(painting.title, painting.id, painting.price, painting.category);
    setSelectedPainting(painting);
    setShowContactForm(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address.');
      return;
    }

    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(formData.phone)) {
      toast.error('Please enter a valid 10-digit Indian phone number.');
      return;
    }

    try {
        const dataToSubmit = {
        paintingId: selectedPainting.id,
        paintingTitle: selectedPainting.title,
        ...formData,
      };
      await paintingAPI.contactSubmit(dataToSubmit);
      toast.success('Your interest has been submitted. We will contact you soon.');
      setShowContactForm(false);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to submit your interest. Please try again.');
    }
  };

  if (loading) {
    return (
      <>
        <SEO 
          title="Loading Painting..."
          description="Loading painting details on Canvaart"
          url={`/painting/${slug}`}
        />
        <div className="spinner" role="status" aria-label="Loading painting details"></div>
      </>
    );
  }

  if (!painting) {
    return (
      <>
        <SEO 
          title="Painting Not Found"
          description="The requested painting could not be found on Canvaart."
          url={`/painting/${slug}`}
        />
        <div className="error-message">Painting not found.</div>
      </>
    );
  }

  const paintingSchema = {
    "@context": "https://schema.org",
    "@type": "Painting",
    "name": painting.title,
    "description": painting.description,
    "image": painting.images?.map(img => `${config.BASE_API_URL}${img}`) || [],
    "artform": painting.category,
    "creator": {
      "@type": "Person",
      "name": "Aanishka Chowdhury"
    },
    "offers": {
      "@type": "Offer",
      "price": painting.price,
      "priceCurrency": "INR",
      "availability": painting.status === 'available' ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "url": `https://canvaart.in/painting/${painting.slug || painting.id}`
    },
    "size": painting.size,
    "dateCreated": painting.year
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://canvaart.in/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Gallery",
        "item": "https://canvaart.in/gallery"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": painting.title,
        "item": `https://canvaart.in/painting/${painting.slug || painting.id}`
      }
    ]
  };

  const seoTitle = `${painting.title} - ${painting.category} Painting`;
  const seoDescription = `Buy ${painting.title}, an original ${painting.category.toLowerCase()} canvas painting by Aanishka Chowdhury. ${painting.description?.substring(0, 100) || ''} Price: ₹${painting.price}. Size: ${painting.size}.`;
  const seoImage = painting.images?.[0] ? `${config.BASE_API_URL}${painting.images[0]}` : '';

  const isAvailable = painting.status === 'available';
  const isAuction = painting.is_auction;

  return (
    <div className="painting-details-page">
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={`${painting.title}, ${painting.category} painting, canvas art, original artwork, buy painting online, Aanishka Chowdhury`}
        image={seoImage}
        url={`/painting/${painting.slug || painting.id}`}
        type="product"
        schema={[paintingSchema, breadcrumbSchema]}
      />
      
      <ToastContainer />
      
      <article className="container" itemScope itemType="https://schema.org/Painting">
        <header className="painting-header">
          <h1 itemProp="name">{painting.title}</h1>
          <p className="painting-category" itemProp="artform">{painting.category}</p>
        </header>

        {/* Trust Badges */}
        <TrustBadges layout="horizontal" />

        <div className="painting-content">
          <div className="painting-images" role="region" aria-label="Painting images">
            {painting.images && painting.images.length > 0 ? (
              painting.images.map((image, index) => (
                <figure
                  key={index}
                  className="painting-image"
                  onClick={() => handleImageClick(`${config.BASE_API_URL}${image}`)}
                >
                  <img
                    src={`${config.BASE_API_URL}${image}`}
                    alt={`${painting.title} - View ${index + 1} of ${painting.images.length}`}
                    className="painting-image"
                    loading="lazy"
                    itemProp="image"
                  />
                  <figcaption className="visually-hidden">{painting.title} image {index + 1}</figcaption>
                </figure>
              ))
            ) : (
              <p>No images available.</p>
            )}
          </div>

          <div className="painting-info">
            <p className="painting-description" itemProp="description">{painting.description}</p>

            {/* Scarcity & Urgency */}
            {isAvailable && !isAuction && (
              <div className="scarcity-banner">
                <span className="scarcity-icon" aria-hidden="true">🔥</span>
                <span><strong>Only 1 original exists</strong> — each painting is unique</span>
              </div>
            )}
            {isAvailable && !isAuction && (
              <div className="interest-banner">
                <span className="interest-dot" aria-hidden="true"></span>
                <span>3 people have contacted about this painting today</span>
              </div>
            )}
            {isAuction && (
              <div className="auction-banner">
                <span className="auction-icon" aria-hidden="true">⏰</span>
                <span><strong>Live Auction</strong> — Place your bid now</span>
              </div>
            )}

            <p className="painting-price" itemProp="offers" itemScope itemType="https://schema.org/Offer">
              Price: <span itemProp="priceCurrency">INR</span> <span itemProp="price">₹{painting.price}</span>
            </p>
            <p className="painting-size">Size: {painting.size}</p>
            <p className="painting-year">Year: {painting.year}</p>
            {painting.is_auction && (
              <p className="painting-auction">
                <strong>Auction:</strong> Starts on {new Date(painting.auction_start_date).toLocaleDateString()} and ends on {new Date(painting.auction_end_date).toLocaleDateString()}
              </p>
            )}
            {painting.featured && <p className="painting-featured">Featured Artwork</p>}
          
            <div className='btnHolder'>
              <button className="btn-contact" onClick={() => handleContactClick(painting)} aria-label={`Contact to buy ${painting.title}`}>
                {isAuction ? 'Contact to Bid' : 'Contact to Buy'}
              </button>
            </div>

            {/* Social Share */}
            <SocialShare 
              title={painting.title}
        url={`/painting/${painting.slug || painting.id}`}
              image={seoImage}
              description={`Original ${painting.category} painting by Aanishka Chowdhury`}
            />

            {/* Trust Info */}
            <div className="trust-info">
              <p><span aria-hidden="true">✓</span> Hand-painted original (not a print)</p>
              <p><span aria-hidden="true">✓</span> Certificate of Authenticity included</p>
              <p><span aria-hidden="true">✓</span> Free shipping across India on orders above ₹5,000</p>
              <p><span aria-hidden="true">✓</span> Secure packaging in protective materials</p>
            </div>
            
            <meta itemProp="dateCreated" content={painting.year} />
            <div itemProp="creator" itemScope itemType="https://schema.org/Person">
              <meta itemProp="name" content="Aanishka Chowdhury" />
            </div>
          </div>
        </div>

        {painting.videos && painting.videos.length > 0 && (
          <section className="painting-videos" aria-label="Painting videos">
            <h2>Videos</h2>
            {painting.videos.map((video, index) => (
              <video key={index} controls className="painting-video" aria-label={`Video ${index + 1} of ${painting.title}`}>
                <source src={`${config.BASE_API_URL}${video}`} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            ))}
          </section>
        )}

        {/* Related Paintings */}
        {relatedPaintings.length > 0 && (
          <section className="related-paintings-section" aria-label="Similar paintings you may like">
            <h2>More {painting.category} Paintings</h2>
            <p className="related-subtitle">If you love this style, you might also like these original artworks</p>
            <div className="related-paintings-grid" role="list">
              {relatedPaintings.map((rp) => (
                <article key={rp.id} className="related-painting-card" role="listitem">
                  <Link to={`/painting/${rp.slug || rp.id}`} aria-label={`View ${rp.title}`}>
                    <div className="related-painting-image">
                      <img 
                        src={`${config.BASE_API_URL}${rp.images[0]}`} 
                        alt={`${rp.title} - ${rp.category} painting`}
                        loading="lazy"
                      />
                    </div>
                    <div className="related-painting-info">
                      <h3>{rp.title}</h3>
                      <p className="related-price">₹{rp.price}</p>
                      <p className="related-size">{rp.size}</p>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
            <div className="view-category-link">
              <Link to="/gallery" className="btn-secondary">Browse All {painting.category} Paintings</Link>
            </div>
          </section>
        )}

        <nav className="back-to-gallery" aria-label="Navigation">
          <Link to="/gallery" className="btn-back" aria-label="Return to art gallery">Back to Gallery</Link>
        </nav>
      </article>
      
      {/* Modal for displaying the selected image */}
      {selectedImage && (
        <div className="image-modal" onClick={closeModal} role="dialog" aria-modal="true" aria-label="Image preview">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <img src={selectedImage} alt={`${painting.title} - Full view`} />
            <button className="close-modal" onClick={closeModal} aria-label="Close image preview">
              &times;
            </button>
          </div>
        </div>
      )}
      
      {showContactForm && (
        <div className="contact-form-modal" role="dialog" aria-modal="true" aria-labelledby="contact-form-title">
          <div className="contact-form-content">
            <h2 id="contact-form-title">Contact to Buy: {selectedPainting.title}</h2>
            <form onSubmit={handleSubmit}>
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleInputChange}
                required
                aria-label="Your name"
              />
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={handleInputChange}
                required
                aria-label="Your email address"
              />
              <input
                type="number"
                name="phone"
                minLength={10}
                maxLength={10}
                placeholder="Your Phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                aria-label="Your phone number"
              />
              <textarea
                name="message"
                placeholder="Your Message"
                value={formData.message}
                onChange={handleInputChange}
                required
                aria-label="Your message"
              ></textarea>
              <button type="submit" className="btn-submit">Submit</button>
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setShowContactForm(false)}
              >
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaintingDetails;
